import React, { useEffect, useState, useRef } from "react";
import { socket } from "../App";
import "../styles/Messaging.css";

const Messaging = ({ roomID }) => {
  const [userData, setUserData] = useState({});
  const [otherUserData, setOtherUserData] = useState({});
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);

  const userToken = sessionStorage.getItem("token");
  const userId = sessionStorage.getItem("user");

  useEffect(() => {
    const fetchUserData = async () => {
      const res = await fetch(`${process.env.REACT_APP_API_PATH}/users/${userId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      });
      const data = await res.json();
      setUserData(data);
    };

    const fetchOtherUserData = async () => {
      const [fromRes, toRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_PATH}/connections?fromUserID=${userId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
        }),
        fetch(`${process.env.REACT_APP_API_PATH}/connections?toUserID=${userId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
        }),
      ]);

      const [fromData, toData] = await Promise.all([fromRes.json(), toRes.json()]);

      const connections = [
        ...(Array.isArray(fromData[0]) ? fromData[0] : []),
        ...(Array.isArray(toData[0]) ? toData[0] : []),
      ];

      const match = connections.find((conn) => String(conn.attributes?.roomid) === String(roomID));
      if (!match) return;

      const otherId = match.fromUserID === parseInt(userId)
        ? match.toUserID
        : match.fromUserID;

      const userRes = await fetch(`${process.env.REACT_APP_API_PATH}/users/${otherId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      });
      const other = await userRes.json();
      setOtherUserData(other);
    };

    fetchUserData();
    fetchOtherUserData();
  }, [userId, roomID, userToken]);

  useEffect(() => {
    const handleMessageReceived = (msg) => {
      setMessages((prev) => [...prev, { ...msg, id: msg.id || Date.now() }]);
    };

    socket.on("/send-message", handleMessageReceived);

    if (roomID) {
      fetch(`${process.env.REACT_APP_API_PATH}/chat-history/history/${roomID}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      })
        .then((res) => res.json())
        .then((result) => {
          setMessages(
            result.map((msg) => ({
              id: msg.id,
              fromUserID: msg.fromUserId,
              message: msg.content,
            }))
          );
        });
    }

    return () => socket.off("/send-message", handleMessageReceived);
  }, [roomID, userToken]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [messages]);

  const handleMessageSend = (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    const payload = {
      fromUserID: parseInt(userData.id),
      toUserID: otherUserData.id,
      message,
      roomID,
    };

    socket.emit("/chat/send", payload);

    setMessages((prev) => [
      ...prev,
      {
        ...payload,
        id: Date.now(),
      },
    ]);

    setMessage("");
  };

  return (
    <div className="chat-room">
      <div className="chat-header">
        <h3>Chatting with {otherUserData.attributes?.username || "Unknown"}</h3>
      </div>
      <div className="chat-body">
        <div className="messages">
          {messages.map((msg) => (
            <div
              className={msg.fromUserID === userData.id ? "message sent" : "message received"}
              key={msg.id}
            >
              <p className="message-sender">
                {msg.fromUserID === userData.id
                  ? userData.attributes?.username || "You"
                  : otherUserData.attributes?.username || "Other"}
              </p>
              <p className="message-text">{msg.message}</p>
            </div>
          ))}
          <div ref={bottomRef}></div>
        </div>
      </div>
      <div className="chat-footer">
        <form className="message-form" onSubmit={handleMessageSend}>
          <input
            type="text"
            className="message-input"
            placeholder="Write a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <button type="submit" className="send-button">
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default Messaging;
